import { useEffect, useRef } from 'react';

import { useT } from '../i18n';
import './ScoreGateDialog.css';

interface ScoreGateDialogProps {
  /** Localised phrases for the score fields still empty ("anul construcției"). */
  missing: string[];
  /** Stay on step 1 and fill the fields in. The safe default: Escape and the backdrop land here. */
  onComplete: () => void;
  /** Advance to step 2 without the score. */
  onSkip: () => void;
}

/**
 * The soft gate between step 1 and step 2.
 *
 * Shown when "Continuați" is pressed while the score-critical fields
 * (construction year, seismic class) are still empty. It never blocks: the
 * owner can always continue, the score simply stays unavailable. Both actions
 * are the same glass pill — the brand carries hierarchy in position and
 * wording, not in a louder button.
 */
export function ScoreGateDialog({ missing, onComplete, onSkip }: ScoreGateDialogProps) {
  const t = useT();
  const panelRef = useRef<HTMLDivElement>(null);
  const stayRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    stayRef.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onComplete();
        return;
      }
      if (event.key === 'Tab') {
        // Two focusable controls; keep the focus loop inside the dialog.
        const nodes = panelRef.current?.querySelectorAll<HTMLElement>('button');
        if (!nodes || nodes.length === 0) return;
        const first = nodes[0]!;
        const last = nodes[nodes.length - 1]!;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus({ preventScroll: true });
    };
  }, [onComplete]);

  return (
    <div
      className="score-gate"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onComplete();
      }}
    >
      <div
        ref={panelRef}
        className="score-gate__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="score-gate-title"
        aria-describedby="score-gate-body"
      >
        <h3 id="score-gate-title">{t.step1.scoreGate.title}</h3>
        <div id="score-gate-body" className="score-gate__body">
          <p>{t.step1.scoreGate.intro(missing.join(' și '))}</p>
          <p className="score-gate__note">{t.step1.scoreGate.note}</p>
        </div>
        <div className="score-gate__actions">
          <button type="button" className="btn" ref={stayRef} onClick={onComplete}>
            {t.step1.scoreGate.stay}
          </button>
          <button type="button" className="btn" onClick={onSkip}>
            {t.step1.scoreGate.skip}
          </button>
        </div>
      </div>
    </div>
  );
}
